import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';
import { debounce } from 'lodash';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { clampValue, hexToRgb, rgbToHex, valueToColor } from '../../shared/colors';
import { Color, Colors } from '../../colorsPalette';

import { T } from '../../helpers/helpers';
import styled from 'styled-components';
import { EyeDropEmpty } from '../../svg/eyeDropEmpty';
import { Icon } from '../../icon';
import { Input } from '../../input/input';

const HexPickerContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-family: sans-serif;

    > div {
        max-width: 195px;
    }
`;

const StyledPicker = styled(HexColorPicker)`
    .react-colorful__saturation {
        border-radius: 8px;
    }

    .react-colorful__hue {
        border-radius: 8px;
        margin-top: 10px;
        height: 8px;
    }

    .react-colorful__pointer {
        width: 20px;
        height: 20px;
        box-shadow: 0 2px 2px rgba(0, 0, 0, 0.15);
    }

    .react-colorful__interactive:focus .react-colorful__pointer {
        transform: translate(-50%, -50%) scale(1.33);
    }
`;

const InputContainer = styled.div`
    display: flex;
    align-items: center;
    grid-gap: 10px;

    input {
        min-width: 0;
    }
`;

const InputLabel = styled.span`
    font-size: 12px;
    line-height: 24px;
    font-family: sans-serif;
`;

const HexValuesContainer = styled.div`
    width: 200px;
`;

const ResetColor = styled.button`
    padding: 0;
    -webkit-appearance: none;
    background-color: #fff;
    border: 0 none;
    cursor: pointer;

    &:hover,
    &:focus {
        opacity: 0.6;
    }

    ${Icon} {
        width: 41px;
    }
`;

const RgbValuesContainer = styled.div`
    width: 200px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 5px;
`;

const HexInput = styled(HexColorInput)`
    flex: 1;
    padding: 4px;
    border: 0 none;
    font-size: 12px;
    line-height: 16px;
    background-color: #f6f6f7;
    font-family: sans-serif;
`;

const RgbInput = styled(Input)`
    display: block;
    width: 100%;
    padding: 4px;
    border: 0 none;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    background-color: #f6f6f7;
    font-family: sans-serif;
    -moz-appearance: textfield; /* fix for mozilla's input rendering */
`;

const ColorsTitle = styled.label`
    display: block;
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 2px;
    text-align: left;
    font-family: sans-serif;
`;

const MAX_RECENT_COLORS = 16;

type RecentColorsState = {
    recentColors: string[];
};

type RecentColorsAction = { type: 'ADD_COLOR'; color: string } | { type: 'REMOVE_COLOR'; index: number };

const recentColorsReducer = (state: RecentColorsState, action: RecentColorsAction): RecentColorsState => {
    switch (action.type) {
        case 'ADD_COLOR':
            if (state.recentColors.length >= MAX_RECENT_COLORS) {
                return {
                    recentColors: [...state.recentColors.slice(1), action.color]
                };
            } else {
                return {
                    recentColors: [...state.recentColors, action.color]
                };
            }
        case 'REMOVE_COLOR':
            return {
                recentColors: [...state.recentColors.slice(0, action.index), ...state.recentColors.slice(action.index + 1)]
            };
        default:
            return state;
    }
};

const AllColorsPicker: FC<{
    color: string;
    usedColors?: string[];
    recentColors?: string[];
    onChange: (color: string) => void;
    onReset?: () => void;
}> = ({ color, usedColors, recentColors, onChange, onReset }) => {
    const [recentColorsState, dispatch] = useReducer(recentColorsReducer, {
        recentColors: recentColors ?? []
    });

    const [preventChange, setPreventChange] = useState(true); // Default true to avoid triggering onChange on mount

    const [r, setR] = useState(hexToRgb(color ?? '#000000').r.toString());
    const [g, setG] = useState(hexToRgb(color ?? '#000000').g.toString());
    const [b, setB] = useState(hexToRgb(color ?? '#000000').b.toString());

    useEffect(() => {
        setPreventChange(false);
    }, []);

    useEffect(() => {
        if (!preventChange) {
            const rbgNewColor = {
                r: valueToColor(r),
                g: valueToColor(g),
                b: valueToColor(b)
            };

            triggerColorChange(rbgNewColor);
        }

        // If the color changed 'color' change, reset the prevent change
        setPreventChange(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [r, g, b]);

    useEffect(() => {
        if (preventChange) return;

        // When the color change, update the rgb values but don't trigger again the change to avoid loop
        if (color && color.length === 7) {
            setPreventChange(true);
            setR(hexToRgb(color).r.toString());
            setG(hexToRgb(color).g.toString());
            setB(hexToRgb(color).b.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [color]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleColorChange = useCallback(
        debounce((color: string) => {
            triggerColorChange(color);
        }, 300),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const triggerColorChange = (color: string | { r: number; g: number; b: number }) => {
        if (typeof color === 'object') {
            //addRecentColor(rgbToHex(color));
            const colorHex = rgbToHex(color);
            const idx = recentColorsState.recentColors.findIndex((x) => x == colorHex);
            if (idx == -1) {
                dispatch({
                    type: 'ADD_COLOR',
                    color: colorHex
                });
                onChange(rgbToHex(color));
            }
        } else {
            const idx = recentColorsState.recentColors.findIndex((x) => x == color);
            if (idx == -1) {
                dispatch({
                    type: 'ADD_COLOR',
                    color: color
                });

                const colorRGB = hexToRgb(color);
                setR(colorRGB.r.toString());
                setG(colorRGB.g.toString());
                setB(colorRGB.b.toString());
            }
            onChange(color);
        }
    };

    return (
        <HexPickerContainer>
            {/* Used colors */}
            {usedColors && (
                <div>
                    <ColorsTitle>{T._('Used colors', 'Admin')}</ColorsTitle>
                    <Colors small>
                        {usedColors?.map((color, idx) => (
                            <Color key={idx + '-' + color} small $hoverable color={color} onClick={() => triggerColorChange(color)} />
                        ))}
                    </Colors>
                </div>
            )}

            {/* Recent colors */}
            {recentColorsState.recentColors.length > 0 && (
                <div>
                    <ColorsTitle>{T._('Recent colors', 'Admin')}</ColorsTitle>
                    <Colors small>
                        {recentColorsState.recentColors.map((color: any, idx: any) => (
                            <Color key={idx + '-' + color} small $hoverable color={color} onClick={() => triggerColorChange(color)} />
                        ))}
                    </Colors>
                </div>
            )}

            <StyledPicker color={color ?? '#000000'} onChange={handleColorChange} />

            <HexValuesContainer>
                <InputContainer>
                    <InputLabel>Hex</InputLabel>
                    <HexInput color={color ?? '#000000'} prefixed onChange={handleColorChange} />
                    {onReset && (
                        <ResetColor onClick={onReset}>
                            <Icon>
                                <EyeDropEmpty />
                            </Icon>
                        </ResetColor>
                    )}
                </InputContainer>
            </HexValuesContainer>

            <RgbValuesContainer>
                <InputContainer>
                    <InputLabel>R</InputLabel>
                    <RgbInput
                        type={'number'}
                        value={r}
                        min={0}
                        max={255}
                        onChange={(e) => {
                            setR(clampValue(e.currentTarget.value));
                        }}
                    />
                </InputContainer>
                <InputContainer>
                    <InputLabel>G</InputLabel>
                    <RgbInput
                        type={'number'}
                        value={g}
                        maxLength={3}
                        onChange={(e) => {
                            setG(clampValue(e.currentTarget.value));
                        }}
                    />
                </InputContainer>
                <InputContainer>
                    <InputLabel>B</InputLabel>
                    <RgbInput
                        type={'number'}
                        value={b}
                        maxLength={3}
                        onChange={(e) => {
                            setB(clampValue(e.currentTarget.value));
                        }}
                    />
                </InputContainer>
            </RgbValuesContainer>
        </HexPickerContainer>
    );
};

export default AllColorsPicker;
